import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { HeroKPICard } from "./HeroKPICard";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { SearchableSelect } from "./searchable-select";
import { Avatar, AvatarImage } from "../../components/ui/avatar";
import { Schedule } from "../../store/appStore";
import { format, addDays, addMonths } from "date-fns";
import {
  FileText,
  Building2,
  TrendingUp,
  Filter,
  CalendarClock,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Eye,
  CreditCard,
} from "lucide-react";
import { formatCurrency, formatLakhs } from "../../lib/utils";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { Spin } from "antd";

type TabFilter = "active" | "draft" | "assigned" | "maturing" | "matured" | "all";
const ITEMS_PER_PAGE = 50;

interface ApiResponse<T> {
  data: T;
  downloaded?: any[];
  message?: string;
  status?: number;
  schedules?: any[]
}

interface LenderCompanyList {
  id: number;
  name: string;
}

const RentalScheduleDashboard = () => {

  const [lenderList, setLenderList] = useState<string[]>([]);

  // Dashboard Schedules State
  const [dashboardSchedules, setDashboardSchedules] = useState<any[]>([]);

  // Filters
  const [tabFilter, setTabFilter] = useState<TabFilter>("active");
  const [customerFilter, setCustomerFilter] = useState<string>("all");
  const [lenderFilter, setLenderFilter] = useState<string>("all");
  const [nachFilter, setNachFilter] = useState<string>("all");

  const [maturingFilter, setMaturingFilter] = useState<string>("all");
  const [frequencyFilter, setFrequencyFilter] = useState<string>("all");
  const [productFilter, setProductFilter] = useState<string>("all");
  const [rsStatusFilter, setRsStatusFilter] = useState<string>("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [nodata, setNoData] = useState(false);

  useEffect(() => {
    getRS_Data();
  }, []);
  const getRS_Data = async () => {
    try {
      const response = await apiService.get<ApiResponse<Schedule[]>>(
        `/rental-schedule/rental-schedules-dashboard`
      );
      const data = response.schedules;
      setDashboardSchedules(Array.isArray(data) ? data : []);
      setNoData(true);
    } catch (error) {
      console.error("Failed to fetch rental schedules:", error);
      toast.error("Failed to load rental schedules");
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft": return "secondary";
      case "Submitted": return "warning";
      case "Approved": return "warning";
      case "In Signing": return "info";
      case "Assigned": return "success";
      case "Matured": return "destructive";
      case "Active": return "success";
      default: return "secondary";
    }
  };
  // Unique customers
  const customers = useMemo(() => {
    const map = new Map<string, string>();
    dashboardSchedules.forEach((s) => {
      const c = s.config?.customer;
      if (c?.customerId && c?.company_name) map.set(c.customerId, c.company_name);
    });
    return Array.from(map.entries()).map(([id, company_name]) => ({ id, company_name }));
  }, [dashboardSchedules]);

  // Unique lenders
  useEffect(() => {
    const fetchLenders = async () => {
      try {
        const response = await apiService.get<{ data: LenderCompanyList[] }>(`/customers/get-lender-company-list`);
        if (response && Array.isArray(response.data)) {
          setLenderList(response.data.map((l) => l.name));
        } else {
          setLenderList([]);
        }
      } catch (error) {
        setLenderList([]);
      }
    };
    fetchLenders();
  }, []);

  // KPIs
  const kpis = useMemo(() => {
    const today = new Date();
    const ninetyDays = addDays(today, 90);

    const activeSchedules = dashboardSchedules.filter((s) => {

      const buy_back_date = s.config?.buy_back_date
        ? new Date(s.config.buy_back_date)
        : null;
      return (
        String(s.status).toLowerCase() === "approved" &&
        buy_back_date &&
        buy_back_date > today
      );

    }).length;
    const totalAssetValue = dashboardSchedules.reduce((sum, s) => {
      return sum + (s.assets?.reduce((a: any, asset: any) => a + (asset.basePrice || 0), 0) || 0);
    }, 0);
    const noaAssigned = dashboardSchedules.filter(item => item.noa?.lenderName != null && item.noa?.lenderName != undefined).length;
    const unassigned = dashboardSchedules.filter(item => item.noa?.lenderName === null || item.noa?.lenderName === undefined).length;

    const maturingIn90Days = dashboardSchedules.filter((s) => {
      if (s.status !== "Active" && s.status !== "Approved") return false;

      if (!s.config || !s.config.buy_back_date) return false;

      const buy_back_date = new Date(s.config.buy_back_date);

      return buy_back_date >= today && buy_back_date <= ninetyDays;
    }).length;

    const maturedSchedules = dashboardSchedules.filter((s) => {
      if (s.status !== "Active" && s.status !== "Approved") return false;

      if (!s.config || !s.config.buy_back_date) return false;

      const buy_back_date = new Date(s.config.buy_back_date);

      return buy_back_date < today;
    }).length;

    return { activeSchedules, totalAssetValue, noaAssigned, unassigned, maturingIn90Days, maturedSchedules, total: dashboardSchedules.length };
  }, [dashboardSchedules]);

  // Tab counts
  const tabCounts = useMemo(() => {
    const today = new Date();
    const ninetyDays = addDays(today, 90);

    const active = dashboardSchedules.filter((s) => {

      const buy_back_date = s.config?.buy_back_date
        ? new Date(s.config.buy_back_date)
        : null;

      return (
        String(s.status).toLowerCase() === "approved"  &&
        buy_back_date &&
        buy_back_date > today
      );

    }).length;
    const draft = dashboardSchedules.filter((s) => s.status === "draft").length;

    const assigned = dashboardSchedules.filter(item => item.noa?.lenderName != null && item.noa?.lenderName != undefined).length;

    const maturing = dashboardSchedules.filter((s) => {
      if (s.status !== "Active" && s.status !== "Approved") return false;

      if (!s.config || !s.config.buy_back_date) return false;

      const buy_back_date = new Date(s.config.buy_back_date);

      return buy_back_date >= today && buy_back_date <= ninetyDays;
    }).length;

    const matured = dashboardSchedules.filter((s) => {
      if (s.status !== "Active" && s.status !== "Approved") return false;

      if (!s.config || !s.config.buy_back_date) return false;

      const buy_back_date = new Date(s.config.buy_back_date);

      return buy_back_date < today;
    }).length;
    return { all: dashboardSchedules.length, active, draft, assigned, maturing, matured };
  }, [dashboardSchedules]);

  // Helper: check maturing window
  const isScheduleMaturingWithin = (schedule: Schedule, months: number) => {
    if (schedule.status !== "Active" && schedule.status !== "Approved") return false;

    const rawDate = schedule.config?.buy_back_date;
    if (!rawDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const buy_back_date = new Date(rawDate);
    buy_back_date.setHours(0, 0, 0, 0);

    if (isNaN(buy_back_date.getTime())) return false;

    const cutoff = addMonths(today, months);
    return buy_back_date >= today && buy_back_date <= cutoff;
  };

  // Filtered schedules
  const filteredSchedules = useMemo(() => {
    const today = new Date();
    const ninetyDays = addDays(today, 90);

    return dashboardSchedules.filter((s) => {
      // Tab filters

      if (tabFilter === "active") {
        const buy_back_date = s.config?.buy_back_date
          ? new Date(s.config.buy_back_date)
          : null;

        // Must be approved-type schedule
        if (String(s.status).toLowerCase() !== "approved") return false;

        // Must have valid date and be in future → Active
        if (!buy_back_date || buy_back_date <= today) return false;
      }
      if (tabFilter === "draft" && String(s.status).toLowerCase() !== "draft") return false;
      if (tabFilter === "assigned" && (s.noa?.lenderName === null || s.noa?.lenderName === undefined)) return false;
      if (tabFilter === "maturing") {
        const buy_back_date = new Date(s.config?.buy_back_date);

        if (
          (s.status !== "Active" && s.status !== "Approved") ||
          !s.config?.buy_back_date ||
          buy_back_date < today ||
          buy_back_date > ninetyDays
        ) {
          return false;
        }
      }
      if (tabFilter === "matured") {
        const buy_back_date = new Date(s.config?.buy_back_date);
        if (s.status !== "Active" && s.status !== "Approved") return false;

        if (!s.config || !s.config.buy_back_date) return false;


        return buy_back_date < today;

      }

      // Inline filters
      if (customerFilter !== "all" && s.config?.customer?.customerId !== customerFilter) return false;
      if (lenderFilter !== "all") {
        if (!s.lenderName || s.lenderName !== lenderFilter) return false;
      }

      // Sheet filters
      if (rsStatusFilter !== "all" && s.status !== rsStatusFilter) return false;
      if (nachFilter !== "all") {
        const isNachActive = s.noaNachActive === true;
        if (nachFilter === "active" && !isNachActive) return false;
        if (nachFilter === "inactive" && isNachActive) return false;
      }
      if (maturingFilter !== "all") {
        let months = 1;
        if (maturingFilter === "3") months = 3;
        else if (maturingFilter === "6") months = 6;
        else if (maturingFilter === "12") months = 12;
        if (!isScheduleMaturingWithin(s, months)) return false;
      }
      if (frequencyFilter !== "all") {
        if ((s.config?.frequency) !== frequencyFilter) return false;
      }
      // Product filter — currently all are "Asset Leasing"
      if (productFilter !== "all") {
        if ((s.assets[0].productType) !== productFilter) return false;

      }

      return true;
    });
  }, [dashboardSchedules, tabFilter, customerFilter, lenderFilter, rsStatusFilter, nachFilter, maturingFilter, frequencyFilter, productFilter]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredSchedules.length / ITEMS_PER_PAGE));
  const paginatedSchedules = filteredSchedules.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleTabChange = (v: string) => { setTabFilter(v as TabFilter); setPage(1); };

  const getRowData = (schedule: Schedule) => {

    const linkedNoA = schedule.noa;
    const assetValue = schedule.assets?.reduce((a, asset) => a + (asset.basePrice || 0), 0) || 0;
    const Product_type = schedule.assets[0].productType;

    const monthlyRental = schedule.lines?.[0]?.baseRentRounded || 0;

    const tenure = schedule.config?.tenureMonths || 0;
    const noaNachActive = schedule?.noaNachActive === true;
    return { assetValue, linkedNoA, lenderName: schedule.lenderName || "", monthlyRental, tenure, noaNachActive, Product_type, avatar: schedule.avatar || "" };
  };

  const clearFilters = () => {
    setCustomerFilter("all");
    setLenderFilter("all");
    setRsStatusFilter("all");
    setNachFilter('all');
    setMaturingFilter("all");
    setFrequencyFilter("all");
    setProductFilter("all");
  };

  const activeFiltersCount = [customerFilter, lenderFilter, rsStatusFilter, nachFilter, maturingFilter, frequencyFilter, productFilter].filter((f) => f !== "all").length;

  const getDisplayStatus = (scheduleData: any) => {
    const today = new Date();
    const lastDate = scheduleData?.config?.buy_back_date
      ? new Date(scheduleData.config.buy_back_date)
      : null;

    scheduleData.status?.toLowerCase() === "draft"
      ? "secondary"
      : scheduleData.status?.toLowerCase() === "approved"
        ? "success"
        : "default";
    const status = scheduleData.status?.toLowerCase();

    // 1. If Draft → show Draft
    if (status === "draft") {
      return "Draft";
    }

    // 2. If Approved + NOA exists
    if (status === "approved" && scheduleData?.noa?.status?.toLowerCase() === "approved") {
      if (lastDate) {
        // Future date → Active
        if (today < lastDate) {
          return "Active";
        }
        // Past date → Matured
        return "Matured";
      }
    }

    // fallback
    return status?.charAt(0).toUpperCase() + status?.slice(1);
  };

  const formatCurrencyINR = (value: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const navigate = useNavigate();

  const handleViewDetails = async (schedule: any) => {
    try {
      const response = await apiService.get<ApiResponse<Schedule[]>>(
        `/rental-schedule/rental-schedules/${schedule.id}`
      );

      if (!response?.schedules) {
        toast.error("Failed to load rental schedules");
        return;
      }

      navigate(`/manage-customer/${schedule.config?.customer?.customerId}`, {
        state: { tab: "schedules-view", schedule: response.schedules[0] },
      });
    } catch (error) {
      console.error("Failed to fetch rental schedules:", error);
      toast.error("Failed to load rental schedules");
    }
  };

  // Updated grid: Customer, RS No., Asset Value, Tenure, Freq, Monthly Rental, Lender, Start Date, Status, NACH, Product, Action
  const gridCols = "grid-cols-[1.6fr_0.8fr_1fr_0.8fr_0.6fr_1fr_1.3fr_0.8fr_0.7fr_0.6fr_0.8fr_55px]";

  return (
    <div className="min-h-screen p-2 w-[100%] bg-white">
      <div className="space-y-5 bg-white p-5">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <HeroKPICard
            title="Active Schedules"
            value={kpis.activeSchedules}
            subtitle={`${kpis.total} total schedules`}
            icon={<FileText className="h-4 w-4" />}
            variant="success"
          />
          <HeroKPICard
            title="Total Asset Value"
            value={formatLakhs(kpis.totalAssetValue)}
            subtitle={`Across ${customers.length} customers`}
            icon={<TrendingUp className="h-4 w-4" />}
            variant="default"
          />
          <HeroKPICard
            title="NoA Assigned"
            value={kpis.noaAssigned}
            subtitle={`${kpis.unassigned} unassigned`}
            icon={<Building2 className="h-4 w-4" />}
            variant="info"
          />
          <HeroKPICard
            title="Maturing in 90 Days"
            value={kpis.maturingIn90Days}
            subtitle="Next 90 days"
            icon={<CalendarClock className="h-4 w-4" />}
            variant="warning"
          />
          <HeroKPICard
            title="Matured Schedules"
            value={kpis.maturedSchedules}
            subtitle="Tenure completed"
            icon={<CalendarCheck className="h-4 w-4" />}
            variant="danger"
          />
        </div>

        {/* Tabs + Filters on same line */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Tabs value={tabFilter} onValueChange={handleTabChange}>
            <TabsList className="h-9">
              {(["active", "draft", "assigned", "maturing", "matured", "all"] as TabFilter[]).map((t) => {
                const labels: Record<TabFilter, string> = { active: "Active", draft: "Draft", assigned: "Assigned", maturing: "Maturing", matured: "Matured", all: "All" };
                const count = tabCounts[t];
                return (
                  <TabsTrigger key={t} value={t} className="text-xs gap-1.5 data-[state=active]:font-semibold cursor-pointer">
                    {labels[t]}
                    <Badge variant={tabFilter === t ? "default" : "success"} className="h-4 min-w-[18px] px-1 text-[10px] leading-none text-black">
                      {count}
                    </Badge>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-2">
            <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Filter className="h-4 w-4" />
                  Filters
                  {activeFiltersCount > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Filter Schedules</SheetTitle>
                  <SheetDescription>Refine the schedule list with advanced filters</SheetDescription>
                </SheetHeader>
                <div className="space-y-4 mt-6">
                  {/* Customer */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Customer Name</label>
                    <SearchableSelect
                      options={[{ value: "all", label: "All Customers" }, ...customers.map((c) => ({ value: c.id, label: c.company_name }))]}
                      value={customerFilter}
                      onValueChange={(v) => { setCustomerFilter(v); setPage(1); }}
                      placeholder="All Customers"
                      searchPlaceholder="Search customers..."
                    />
                  </div>
                  {/* Lender */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Lender Name</label>
                    <SearchableSelect
                      options={[{ value: "all", label: "All Lenders" }, ...lenderList.map((l) => ({ value: l, label: l }))]}
                      value={lenderFilter}
                      onValueChange={(v) => { setLenderFilter(v); setPage(1); }}
                      placeholder="All Lenders"
                      searchPlaceholder="Search lenders..."
                    />
                  </div>
                  {/* NACH Status */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">NACH Status</label>
                    <Select
                      value={nachFilter === 'all' ? 'all' : nachFilter === 'active' ? 'active' : nachFilter === 'inactive' ? 'inactive' : 'none'}
                      onValueChange={(v) => {
                        if (v === 'all') setNachFilter('all');
                        else if (v === 'active') setNachFilter('active');
                        else if (v === 'inactive') setNachFilter('inactive');

                      }}
                    >
                      <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="active">NACH Active</SelectItem>
                        <SelectItem value="inactive">NACH Inactive</SelectItem>
                        {/* <SelectItem value="none">No NACH</SelectItem> */}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Maturing In */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Maturing In</label>
                    <Select value={maturingFilter} onValueChange={setMaturingFilter}>
                      <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="1">This Month</SelectItem>
                        <SelectItem value="3">3 Months</SelectItem>
                        <SelectItem value="6">6 Months</SelectItem>
                        <SelectItem value="12">1 Year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Frequency */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Frequency</label>
                    <Select value={frequencyFilter} onValueChange={setFrequencyFilter}>
                      <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Product */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Product</label>
                    <Select value={productFilter} onValueChange={setProductFilter}>
                      <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Products</SelectItem>
                        <SelectItem value="Asset Leasing">Asset Leasing</SelectItem>
                        <SelectItem value="Sale and Leaseback">Sale and Leaseback</SelectItem>

                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button variant="outline" onClick={clearFilters} className="flex-1">Clear All</Button>
                    <Button onClick={() => setFilterOpen(false)} className="flex-1">Apply</Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            <span className="text-xs text-muted-foreground">
              {filteredSchedules.length} schedule{filteredSchedules.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Table — CSS Grid */}
        <div className="rounded-md border overflow-x-auto">
          {/* Header */}
          <div className={`grid ${gridCols} gap-0 text-[11px] uppercase tracking-wider font-medium text-muted-foreground bg-muted/50 backdrop-blur-sm sticky top-0 z-10 border-b`}>
            <div className="px-3 py-2 text-center">Customer</div>
            <div className="px-3 py-2">RS No.</div>
            <div className="px-3 py-2 text-center">Product</div>
            <div className="px-3 py-2">Asset Value</div>
            <div className="px-3 py-2 text-center">Tenure</div>
            <div className="px-3 py-2 text-center">Freq</div>
            <div className="px-3 py-2 text-center">Monthly Rental</div>
            <div className="px-3 py-2">Lender</div>
            <div className="px-3 py-2 text-center">Start Date</div>
            <div className="px-3 py-2 text-center">Status</div>
            <div className="px-3 py-2 text-center">NACH</div>
            <div className="px-3 py-2 text-center">Action</div>
          </div>

          {/* Rows */}
          {paginatedSchedules.length === 0 && nodata ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <FileText className="h-10 w-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">No schedules found</p>
              <p className="text-xs mt-1">Try adjusting your filters or tab selection</p>
            </div>
          ) : paginatedSchedules.length === 0 && nodata === false ? (
            <div className="fixed inset-0 bg-opacity-30 z-50 flex items-center justify-center">
              <div className="bg-white p-5 rounded-lg shadow-lg flex flex-col items-center">
                <Spin size="large" />
                <p className="mt-3 text-gray-700">Loading data...</p>
              </div>
            </div>

          ) : (
            paginatedSchedules.map((schedule) => {
              const data = getRowData(schedule);
              return (
                <div
                  key={schedule.id}
                  className={`grid ${gridCols} gap-0 text-sm border-b last:border-b-0 hover:bg-muted/40 transition-colors items-center`}
                >
                  {/* Customer */}
                  <div className="px-3 py-2.5 font-medium truncate">
                    {schedule.config?.customer?.company_name || "-"}
                  </div>
                  {/* RS No. */}
                  <div className="px-3 py-2.5">
                    <Link className="text-primary hover:underline font-medium text-xs">
                      RS NO - {schedule.displayId || schedule.id.slice(0, 8)}
                    </Link>
                  </div>
                  {/* Product */}
                  <div className="px-3 py-2.5 font-medium truncate">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 whitespace-nowrap">
                      {data.Product_type}
                    </Badge>
                  </div>
                  {/* Asset Value */}
                  <div className="px-3 py-2.5 text-right font-mono tabular-nums text-xs">
                    {formatCurrency(data.assetValue)}
                  </div>
                  {/* Tenure */}
                  <div className="px-3 py-2.5 text-center text-xs">
                    <span>{data.tenure}M</span>

                  </div>
                  {/* Frequency */}
                  <div className="px-3 py-2.5 text-center">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {schedule.config?.frequency ? schedule.config.frequency.charAt(0).toUpperCase() + schedule.config.frequency.slice(1).toLowerCase() : ""}
                    </Badge>
                  </div>
                  {/* Monthly Rental */}
                  <div className="px-3 py-2.5 text-center">
                    {formatCurrencyINR(data.monthlyRental)}
                  </div>
                  {/* Lender with Avatar */}
                  <div className="px-3 py-2.5 flex items-center gap-2 min-w-0">
                    {data.lenderName ? (
                      <>
                        <Avatar
                          className="rounded-lg"
                        >
                          <AvatarImage
                            src={
                              data.linkedNoA?.lenderAvatar
                                ? data.linkedNoA?.lenderAvatar.startsWith("http")
                                  ? data.linkedNoA?.lenderAvatar
                                  : `${import.meta.env.VITE_API_BASE_URL}/${data.linkedNoA?.lenderAvatar
                                  }`
                                : ""}
                            alt={data.lenderName}
                            className="object-contain" />
                        </Avatar>
                        <span className="truncate text-xs">{data.lenderName}</span>
                      </>
                    ) : (
                      <span className="px-3 py-2.5 flex items-center gap-2 min-w-0">—</span>
                    )}
                  </div>
                  {/* Start Date */}
                  <div className="px-3 py-2.5 text-center text-xs text-muted-foreground">
                    {schedule.config?.commencementDate
                      ? format(new Date(schedule.config.commencementDate), "dd MMM yy")
                      : "—"}
                  </div>
                  {/* Status */}
                  <div className="px-3 py-2.5 text-center">
                    <Badge variant={getStatusColor(getDisplayStatus(schedule))} className="text-[10px]">
                      {getDisplayStatus(schedule)}
                    </Badge>
                  </div>
                  {/* NACH */}
                  <div className="px-3 py-2.5 text-center">
                    {data.noaNachActive === true ? (
                      <Badge variant="success" className="text-[10px] gap-1">
                        <CreditCard className="h-2.5 w-2.5" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">Inactive</Badge>
                    )}
                  </div>
                  {/* Action — View */}
                  <div className="px-3 py-2.5 text-center">
                    {/* <Link  > */}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewDetails(schedule)}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    {/* </Link> */}
                  </div>
                </div>
              );
            })
          )}

        </div>

        {/* Pagination */}
        {filteredSchedules.length > ITEMS_PER_PAGE && (
          <div className="flex items-center justify-between py-2">
            <span className="text-xs text-muted-foreground">
              Showing {(page - 1) * ITEMS_PER_PAGE + 1}–{Math.min(page * ITEMS_PER_PAGE, filteredSchedules.length)} of {filteredSchedules.length}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="h-7 px-2">
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="h-7 px-2">
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RentalScheduleDashboard;
